/**
 * SMS Count Hook
 * Custom hook to fetch and manage inbound SMS count
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { smsCountService } from '@/services/features/SMSCountService';

export const useSMSCount = () => {
  const [smsCount, setSmsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user, hasuraToken } = useAuth();

  const fetchSMSCount = useCallback(async () => {
    if (!user?.id || !hasuraToken) {
      setSmsCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let count: number;
      
      // Check if user is admin (PA, CA, EA)
      const isAdmin = ['PA', 'CA', 'EA'].includes(user.type);
      
      if (isAdmin) {
        count = await smsCountService.getSMSCountForAdmin(hasuraToken);
      } else {
        count = await smsCountService.getSMSCountForUser(Number(user.id), hasuraToken);
      }

      setSmsCount(count);
    } catch (err) {
      console.error('Failed to fetch SMS count:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch SMS count');
      setSmsCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.type, hasuraToken]);

  useEffect(() => {
    fetchSMSCount();
  }, [fetchSMSCount]);

  return {
    smsCount,
    loading,
    error,
    refetch: fetchSMSCount
  };
};